import { useState, useEffect, use } from 'react';
import { collection, query, orderBy, limit, onSnapshot, Timestamp } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { format } from 'date-fns';
import { Book, Info, Search, Clock, History, PenTool, Compass } from 'lucide-react';
import type { ActivityType } from '@/lib/activity-logger';
import { PassageDataContext } from '@/data/passage/passage-data-context';

interface ActivityLog {
    id: string;
    type: ActivityType;
    description: string;
    path: string;
    timestamp: Date;
    metadata?: any;
}

export function UserActivityFeed({ userId, limitCount = 50 }: { userId: string, limitCount?: number }) {
    const [activities, setActivities] = useState<ActivityLog[]>([]);
    const [loading, setLoading] = useState(true);
    const { get: getPassage } = use(PassageDataContext);

    useEffect(() => {
        if (!userId) return;

        const db = getFirestore(getApp());
        const activityRef = collection(db, 'users', userId, 'activity');
        const q = query(activityRef, orderBy('timestamp', 'desc'), limit(limitCount));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const logs = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toDate() : new Date(),
                } as ActivityLog;
            });
            setActivities(logs);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId, limitCount]);

    const getIcon = (type: ActivityType) => {
        switch (type) {
            case 'passage_view': return <Book className="h-4 w-4 text-blue-500" />;
            case 'context_view': return <Info className="h-4 w-4 text-purple-500" />;
            case 'search': return <Search className="h-4 w-4 text-gray-500" />;
            case 'guided_study_step': return <Compass className="h-4 w-4 text-green-500" />;
            case 'manuscript_mark': return <PenTool className="h-4 w-4 text-orange-500" />;
            default: return <History className="h-4 w-4 text-gray-400" />;
        }
    };

    if (loading) {
        return <div className="flex justify-center py-8"><Clock className="animate-spin h-6 w-6 text-muted-foreground opacity-30" /></div>;
    }

    if (activities.length === 0) {
        return (
            <div className="text-center py-12 px-6">
                <History className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No recent activity recorded for this user.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-sm font-semibold flex items-center gap-2 px-1">
                <History className="h-4 w-4" />
                Recent Exploration Log
            </h3>
            <div className="space-y-3">
                {activities.map((log) => {
                    // Try to resolve passage reference from ID
                    let passageRef = '';
                    const passageId = log.metadata?.passageId;
                    if (passageId) {
                        const p = getPassage(passageId);
                        if (p) {
                            passageRef = `${p.book} ${p.ref}`;
                        }
                    }

                    return (
                        <div key={log.id} className="group relative pl-6 pb-2 border-l border-muted/60 last:pb-0">
                            <div className="absolute left-[-9px] top-1 bg-background border border-muted p-1 rounded-full group-last:border-transparent">
                                {getIcon(log.type)}
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-medium leading-tight">
                                        {log.description}
                                        {passageRef && (
                                            <span className="ml-1 text-primary font-semibold">
                                                ({passageRef})
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                        {format(log.timestamp, 'h:mm:ss a')}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70">
                                    <span>{format(log.timestamp, 'MMM d')}</span>
                                    <span className="opacity-30">•</span>
                                    <span className="font-mono truncate max-w-[200px]">{log.path}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
